import React, { useState, useContext, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";

import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import Box from "@material-ui/core/Box";
import Grow from "@material-ui/core/Grow";
import Paper from "@material-ui/core/Paper";
import Popper from "@material-ui/core/Popper";
import MenuItem from "@material-ui/core/MenuItem";
import MenuList from "@material-ui/core/MenuList";
import { CircularProgress, makeStyles } from "@material-ui/core";
import { Replay } from "@material-ui/icons";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";
import TransferTicketModal from "../TransferTicketModal";
import ContactDrawer from "../ContactDrawer";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import apiV2 from "../../services/apiV2";
import { emitTicketRealtime } from "../../helpers/ticketRealtimeEvents";
import PermissionModal from "../PermissionModal";
import AcceptTicketWithouSelectQueue from "../AcceptTicketWithoutQueueModal";

const options = [
	{
		text: i18n.t("messagesList.header.buttons.solverWithoutGoodbye"),
		value: "solveWithoutGoodbye",
	},
	{ text: i18n.t("contactModal.form.mainInfo"), value: "seeMainInfo" },
	{ text: i18n.t("messagesList.header.buttons.return"), value: "return" },
	{ text: i18n.t("ticketOptionsMenu.transfer"), value: "transfer" },
	{ text: i18n.t("ticketOptionsMenu.delete"), value: "delete" },
	// { text: "Enviar formulário de avaliação", value: "rating" },
];

const useStyles = makeStyles((theme) => ({
	popper: {
		position: "absolute",
		height: "20em",
		zIndex: 1300,
	},
	btnGroup: {
		[theme.breakpoints.down("sm")]: {
			transform: "scale(0.9)",
			width: "33vw",
		},
	},
}));

export default function TicketActionButtons({
	ticket,
	contact,
	tailwickLayout = false,
	onOpenContactDrawer,
	onTicketUpdated,
}) {
	const classes = useStyles();
	const [loading, setLoading] = useState(false);
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [permissionOpen, setPermissionOpen] = useState(false);
	const [open, setOpen] = React.useState(false);
	const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const { user } = useContext(AuthContext);
	const anchorRef = React.useRef(null);
	const tailwickMenuRef = useRef(null);
	const history = useHistory();
	const [
		acceptTicketWithouSelectQueueOpen,
		setAcceptTicketWithouSelectQueueOpen,
	] = useState(false);


	const [connection, setConnection] = useState(null);
	const [isConnected, setIsConnected] = useState(false);

	const handleDrawerOpen = () => {
		setDrawerOpen(true);
	};

	const handleDrawerClose = () => {
		setDrawerOpen(false);
	};


	useEffect(() => {
		if (!ticket.whatsappId) return;

		const fetchConnection = async () => {
			try {
				const { data } = await api.get(`/hub-ticketId/${ticket.whatsappId}`);
				setConnection(data || null);
				setIsConnected(!!data);
			} catch (err) {
				setConnection(null);
				setIsConnected(false);
			}
		};

		fetchConnection();
	}, [ticket.whatsappId]);


	const handleUpdateTicketStatus = async (status, userId, sendMessage) => {
		if (loading) return;
		setLoading(true);
		try {
			const { data } = await apiV2.put(`/tickets/${ticket.id}`, {
				status,
				userId: userId || null,
				sendMessage,
			});
			emitTicketRealtime({
				action: "update",
				ticket: data,
				previousStatus: ticket.status,
			});
			if (ticket.status !== data.status) {
				emitTicketRealtime({
					action: "delete",
					ticket: data,
					previousStatus: ticket.status,
				});
			}
			onTicketUpdated?.(data);
			if (status === "open") {
				if (history.location.pathname !== `/tickets/${ticket.id}`) {
					history.push(`/tickets/${ticket.id}`);
				}
			} else {
				history.push("/tickets");
			}
		} catch (err) {
			toastError(err);
		} finally {
			setLoading(false);
		}
	};

	const handleDeleteTicket = async () => {
		try {
			await api.delete(`/tickets/${ticket.id}`);
			history.push("/tickets");
			setLoading(false);
		} catch (err) {
			toastError(err);
		}
	};

	const sendRating = async () => {
		try {
			const { data: url } = await api.post(`/rating-url/${ticket.id}`);

			const message = {
				read: 1,
				fromMe: true,
				mediaUrl: "",
				body: `Avalie o nosso atendimento atendimento: ${url}`,
			};
			if (isConnected) {
				await api.post(`/hub-message/${ticket.id}`, message);
			} else {
				await api.post(`/messages/${ticket.id}`, message);
			}

			setLoading(false);
		} catch (err) {
			toastError(err);
		}
	};

	const handleMenuItemClick = (value) => {
		setLoading(true);
		switch (value) {
			case "resolve":
				handleUpdateTicketStatus("closed", user.id, true);
				break;
			case "solveWithoutGoodbye":
				handleUpdateTicketStatus("closed", user?.id, false);
				break;
			case "return":
				handleUpdateTicketStatus("pending", null, false);
				break;
			case "delete":
				user.profile === "admin"
					? setConfirmationOpen(true)
					: setPermissionOpen(true);
				setLoading(false);
				break;
			case "transfer":
				setTransferTicketModalOpen(true);
				setLoading(false);
				break;
			case "seeMainInfo":
				setLoading(false);
				if (tailwickLayout && onOpenContactDrawer) {
					onOpenContactDrawer();
				} else {
					handleDrawerOpen();
				}
				break;
			case "rating":
				sendRating();
			default:
				break;
		}
		setOpen(false);
	};

	const handleToggle = () => {
		setOpen((prevOpen) => !prevOpen);
	};

	const handleClose = (event) => {
		if (anchorRef.current && anchorRef.current.contains(event.target)) {
			return;
		}

		setOpen(false);
	};

	useEffect(() => {
		if (!tailwickLayout || !open) return undefined;

		const handleClickOutside = (event) => {
			if (
				tailwickMenuRef.current &&
				!tailwickMenuRef.current.contains(event.target)
			) {
				setOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [open, tailwickLayout]);

	const handleAcceptOrReopen = () => {
		if (ticket.queueId) {
			handleUpdateTicketStatus("open", user.id, false);
		} else {
			setAcceptTicketWithouSelectQueueOpen(true);
		}
	};

	if (tailwickLayout) {
		return (
			<>
				<PermissionModal open={permissionOpen} onClose={setPermissionOpen} />
				{!ticket.queueId && (
					<AcceptTicketWithouSelectQueue
						modalOpen={acceptTicketWithouSelectQueueOpen}
						onClose={() => setAcceptTicketWithouSelectQueueOpen(false)}
						ticketId={ticket.id}
					/>
				)}
				{ticket.status === "pending" || ticket.status === "closed" ? (
					<button
						type="button"
						className="tailwick-ticket-actions__single btn btn-sm btn-primary inline-flex items-center gap-1.5"
						disabled={loading}
						onClick={handleAcceptOrReopen}
					>
						{loading ? (
							<span
								className="size-4 animate-spin rounded-full border-2 border-white border-t-transparent"
								aria-hidden
							/>
						) : (
							ticket.status === "closed" && (
								<svg
									className="size-4 shrink-0"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									aria-hidden
								>
									<path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
									<path d="M21 3v5h-5" />
									<path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
									<path d="M8 16H3v5" />
								</svg>
							)
						)}
						{ticket.status === "pending"
							? i18n.t("messagesList.header.buttons.accept")
							: i18n.t("messagesList.header.buttons.reopen")}
					</button>
				) : (
					<div
						className="tailwick-ticket-actions relative"
						ref={tailwickMenuRef}
					>
						<div className="tailwick-ticket-actions__split">
							<button
								type="button"
								className="tailwick-ticket-actions__main btn btn-sm btn-primary"
								disabled={loading}
								onClick={() => handleMenuItemClick("resolve")}
							>
								{loading ? (
									<span
										className="size-4 animate-spin rounded-full border-2 border-white border-t-transparent"
										aria-hidden
									/>
								) : (
									i18n.t("messagesList.header.buttons.resolve")
								)}
							</button>
							<button
								type="button"
								className="tailwick-ticket-actions__toggle btn btn-sm btn-primary"
								disabled={loading}
								aria-expanded={open}
								aria-haspopup="menu"
								onClick={handleToggle}
							>
								<svg
									className={`size-4 transition-transform${open ? " rotate-180" : ""}`}
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									aria-hidden
								>
									<polyline points="6 9 12 15 18 9" />
								</svg>
							</button>
						</div>
						{open && (
							<div
								className="tailwick-ticket-actions__menu"
								role="menu"
								id="tailwick-ticket-actions-menu"
							>
								{options.map((option) => (
									<button
										key={option.value}
										type="button"
										role="menuitem"
										className="tailwick-ticket-actions__menu-item"
										onClick={() => handleMenuItemClick(option.value)}
									>
										{option.text}
									</button>
								))}
							</div>
						)}
					</div>
				)}
				{!tailwickLayout && drawerOpen && (
					<ContactDrawer
						loading={loading}
						contact={contact}
						open={drawerOpen}
						handleDrawerClose={handleDrawerClose}
					/>
				)}
				<ConfirmationModal
					title={`${i18n.t("ticketOptionsMenu.confirmationModal.title")}${ticket.id} ${i18n.t("ticketOptionsMenu.confirmationModal.titleFrom")} ${ticket.contact?.name ?? ""}?`}
					open={confirmationOpen}
					onClose={setConfirmationOpen}
					onConfirm={handleDeleteTicket}
				>
					{i18n.t("ticketOptionsMenu.confirmationModal.message")}
				</ConfirmationModal>
				<TransferTicketModal
					modalOpen={transferTicketModalOpen}
					onClose={() => setTransferTicketModalOpen(false)}
					ticketid={ticket.id}
					ticketWhatsappId={ticket.whatsappId}
					ticketChannel={ticket.channel}
				/>
			</>
		);
	}

	return (
		<>
			<PermissionModal open={permissionOpen} onClose={setPermissionOpen} />
			{!ticket.queueId && (
				<AcceptTicketWithouSelectQueue
					modalOpen={acceptTicketWithouSelectQueueOpen}
					onClose={(e) => setAcceptTicketWithouSelectQueueOpen(false)}
					ticketId={ticket.id}
				/>
			)}
			{ticket.status === "pending" || ticket.status === "closed" ? (
				<ButtonWithSpinner
					loading={loading}
					startIcon={ticket.status === "closed" && <Replay />}
					size="small"
					onClick={() => {
						ticket.queueId
							? handleUpdateTicketStatus("open", user.id, false)
							: setAcceptTicketWithouSelectQueueOpen(true);
					}}
				// onClick={() => handleUpdateTicketStatus("open", null, false)}
				>
					{ticket.status === "pending"
						? i18n.t("messagesList.header.buttons.accept")
						: i18n.t("messagesList.header.buttons.reopen")}
				</ButtonWithSpinner>
			) : (
				<Grid
					container
					direction="column"
					alignItems="center"
					alignContent="flex-end"
				>
					<Grid item xs={12}>
						<ButtonGroup
							variant="contained"
							color="primary"
							ref={anchorRef}
							aria-label="split button"
							zindex="tooltip"
							className={classes.btnGroup}
						>
							<Button onClick={() => handleMenuItemClick("resolve")}>
								{loading ? (
									<CircularProgress color="inherit" />
								) : (
									i18n.t("messagesList.header.buttons.resolve")
								)}
							</Button>
							<Button
								color="primary"
								size="small"
								aria-controls={open ? "split-button-menu" : undefined}
								aria-expanded={open ? "true" : undefined}
								aria-label="select merge strategy"
								aria-haspopup="menu"
								onClick={handleToggle}
							>
								<ArrowDropDownIcon />
							</Button>
						</ButtonGroup>
						<Popper
							open={open}
							anchorEl={anchorRef.current}
							role={undefined}
							transition
							disablePortal={false}
							className={classes.popper}
						>
							{({ TransitionProps, placement }) => (
								<Grow
									{...TransitionProps}
									style={{
										transformOrigin:
											placement === "bottom" ? "center top" : "center bottom",
									}}
								>
									<Paper>
										<ClickAwayListener onClickAway={handleClose}>
											<MenuList id="split-button-menu">
												{options.map((option, index) => (
													<MenuItem
														key={option.text}
														onClick={(event) =>
															handleMenuItemClick(option.value)
														}
													>
														{option.text}
													</MenuItem>
												))}
											</MenuList>
										</ClickAwayListener>
									</Paper>
								</Grow>
							)}
						</Popper>
					</Grid>
				</Grid>
			)}
			{/* MODAL BOX - DRAWERS AND MODALS SHOULD BE INSIDE AND TOGGLED ON AND OFF BASED ON USER INTERACTION */}
			<Box>
				{!tailwickLayout && drawerOpen && (
					<ContactDrawer
						loading={loading}
						contact={contact}
						open={drawerOpen}
						handleDrawerClose={handleDrawerClose}
					/>
				)}
				<ConfirmationModal
					title={`${i18n.t("ticketOptionsMenu.confirmationModal.title")}${ticket.id
						} ${i18n.t("ticketOptionsMenu.confirmationModal.titleFrom")} ${ticket.contact?.name ?? ""
						}?`}
					open={confirmationOpen}
					onClose={setConfirmationOpen}
					onConfirm={handleDeleteTicket}
				>
					{i18n.t("ticketOptionsMenu.confirmationModal.message")}
				</ConfirmationModal>
				<TransferTicketModal
					modalOpen={transferTicketModalOpen}
					onClose={() => {
						setTransferTicketModalOpen(false);
					}}
					ticketid={ticket.id}
					ticketWhatsappId={ticket.whatsappId}
					ticketChannel={ticket.channel}
				/>
			</Box>
		</>
	);
}
